/* ==========================================================================
   CONTACT PAGE
   Three independent pieces:
     1. Ambient background - a slow, dim molten-gold shader behind the whole
        section (same visual language as the hero, tuned way down so it
        reads as atmosphere, not competition for the text/form on top).
     2. Hero glow - the livelier, mouse-reactive gold field from the home
        hero (theme.js GoldField, same shader cloned here since that module
        is bound to .stage, which doesn't exist on this page). Hidden until
        the diamond is hovered, then fades in over the ambient layer and
        back out again.
     3. Diamond coin-flip - a one-shot 360deg spin on hover/focus (CSS only),
        same pattern as the brand-story timeline's node diamonds.
   Reduced-motion and no-WebGL both skip the ambient/hero-glow shaders
   entirely; the form itself has no dependency on either.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-contact-page]');
  if (!root) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var form = root.querySelector('[data-contact-form]');
  var ambientCanvas = root.querySelector('[data-ambient-canvas]');
  var heroGlowCanvas = root.querySelector('[data-hero-glow-canvas]');
  var diamondTrigger = root.querySelector('[data-diamond-trigger]');
  var submitBtn = root.querySelector('[data-contact-submit]');

  function hashNoiseGlsl() {
    return [
      'float hash(vec2 p){ p=fract(p*vec2(234.34,435.345)); p+=dot(p,p+34.23); return fract(p.x*p.y); }',
      'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);',
      '  return mix(mix(hash(i),hash(i+vec2(1.,0.)),u.x), mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),u.x),u.y); }',
      'float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<4;i++){ v+=a*noise(p); p*=2.0; a*=0.5; } return v; }'
    ].join('\n');
  }

  var VERT = 'varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,0.0,1.0); }';

  /* ======================================================================
     1. AMBIENT BACKGROUND
     ====================================================================== */
  var AMBIENT_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform float uTime;',
    hashNoiseGlsl(),
    'void main(){',
    '  vec2 p = vUv * 3.2;',
    '  float t = uTime * 0.035;',
    '  vec2 q = vec2(fbm(p + vec2(0.0, t)), fbm(p + vec2(5.2, -t)));',
    '  float n = fbm(p + q * 2.0);',
    '  vec3 gold = vec3(0.79, 0.63, 0.37);',
    '  float f = pow(n, 3.2);',
    '  vec3 col = gold * f * 0.4;',
    '  gl_FragColor = vec4(col, f * 0.22);',
    '}'
  ].join('\n');

  function initAmbient() {
    if (REDUCED || typeof THREE === 'undefined' || !ambientCanvas) return;
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: ambientCanvas, alpha: true, antialias: false });
    } catch (err) {
      return;
    }
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));

    var scene = new THREE.Scene();
    var camera = new THREE.Camera();
    var material = new THREE.ShaderMaterial({
      uniforms: { uTime: { value: 0 } },
      vertexShader: VERT, fragmentShader: AMBIENT_FRAG, transparent: true
    });
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));

    function resize() {
      var w = root.clientWidth, h = root.clientHeight;
      renderer.setSize(w, h, false);
    }
    new ResizeObserver(resize).observe(root);
    resize();

    var running = false, rafId = null, start = null;
    function frame(ts) {
      if (!running) return;
      if (start === null) start = ts;
      material.uniforms.uTime.value = (ts - start) * 0.001;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(frame);
    }
    function startLoop() { if (!running) { running = true; start = null; rafId = requestAnimationFrame(frame); } }
    function stopLoop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? startLoop() : stopLoop();
      }, { threshold: 0.01 }).observe(root);
    } else {
      startLoop();
    }
    document.addEventListener('visibilitychange', function () { document.hidden ? stopLoop() : startLoop(); });
  }

  /* ======================================================================
     2. HERO GLOW - the home hero's molten-gold field (theme.js GoldField),
        same shader, cloned here rather than imported since that module
        queries .stage directly. Mouse-reactive over the whole section while
        hovering the diamond; the render loop only runs while visible, and
        the fade in/out is a plain CSS opacity transition on the canvas.
     ====================================================================== */
  var HERO_GLOW_FRAG = [
    'precision highp float;',
    'varying vec2 vUv;',
    'uniform float uTime; uniform vec2 uMouse; uniform vec2 uRes; uniform float uEnergy;',
    hashNoiseGlsl(),
    'void main(){',
    '  float aspect=uRes.x/max(uRes.y,1.0);',
    '  vec2 p=vUv; p.x*=aspect;',
    '  float t = uTime*0.05;',
    '  vec2 q=vec2(fbm(p*1.6+vec2(0.0,t)), fbm(p*1.6+vec2(5.2,-t)));',
    '  vec2 r=vec2(fbm(p*1.6+3.0*q+vec2(1.7,9.2)+t*0.5), fbm(p*1.6+3.0*q+vec2(8.3,2.8)-t*0.5));',
    '  float n=fbm(p*1.6+2.5*r);',
    '  vec2 m=uMouse; m.x*=aspect; float glow=smoothstep(0.9,0.0,distance(p,m))*0.6;',
    '  vec3 c0=vec3(0.016,0.016,0.021);',
    '  vec3 c1=vec3(0.105,0.078,0.040);',
    '  vec3 c2=vec3(0.540,0.400,0.205);',
    '  vec3 c3=vec3(0.880,0.775,0.560);',
    '  float f=pow(n,1.78);',
    '  vec3 gold=mix(c0,c1,smoothstep(0.16,0.52,f));',
    '  gold=mix(gold,c2,smoothstep(0.48,0.80,f));',
    '  gold=mix(gold,c3,smoothstep(0.78,0.98,f)*0.85);',
    '  float streak=pow(max(0.0,sin((r.x+r.y)*6.2831+t*3.0)),8.0);',
    '  gold+=c3*streak*0.13;',
    '  vec3 base=mix(vec3(0.022,0.017,0.011), vec3(0.005,0.005,0.008), smoothstep(0.0,0.95,length((vUv-vec2(0.5,0.62))*vec2(1.0,1.12))));',
    '  float e=uEnergy*uEnergy*(3.0-2.0*uEnergy);',
    '  vec3 col=mix(base, gold, e);',
    '  col+=vec3(0.55,0.42,0.22)*glow*e*(0.4+f);',
    '  vec2 vc=vUv-0.5; float vig=smoothstep(0.99,0.28,length(vc*vec2(1.05,1.25)));',
    '  col*=mix(0.42,1.02,vig);',
    '  gl_FragColor=vec4(col,1.0);',
    '}'
  ].join('\n');

  function initHeroGlow() {
    if (REDUCED || typeof THREE === 'undefined' || !heroGlowCanvas || !diamondTrigger) return;
    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas: heroGlowCanvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
    } catch (err) {
      return;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));

    var scene = new THREE.Scene();
    var camera = new THREE.Camera();
    var material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new THREE.Vector2(0.5, 0.6) },
        uRes: { value: new THREE.Vector2(1, 1) },
        uEnergy: { value: 0.55 } // overwritten by the envelope in frame() once a cycle starts
      },
      vertexShader: VERT, fragmentShader: HERO_GLOW_FRAG
    });
    scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));

    function resize() {
      var w = root.clientWidth, h = root.clientHeight;
      renderer.setSize(w, h, false);
      material.uniforms.uRes.value.set(w, h);
    }
    new ResizeObserver(resize).observe(root);
    resize();

    var pointer = { x: 0.5, y: 0.4 };
    root.addEventListener('pointermove', function (e) {
      var r = root.getBoundingClientRect();
      pointer.x = (e.clientX - r.left) / r.width;
      pointer.y = (e.clientY - r.top) / r.height;
    }, { passive: true });

    // CYCLE_MS must match the CSS animation-duration on
    // .contact-page__hero-glow.is-active (contact-page.css) - it drives both
    // the opacity build/fade there and the uEnergy envelope here, so the
    // shader's own intensity builds and recedes in step with the fade
    // rather than sitting at a flat level under a changing alpha.
    var CYCLE_MS = 5000;
    var BASE_ENERGY = 0.55, PEAK_ENERGY = 0.8;
    var running = false, rafId = null, start = null, cycleStart = null;
    function frame(ts) {
      if (!running) return;
      if (start === null) start = ts;
      if (cycleStart === null) cycleStart = ts;
      material.uniforms.uTime.value = (ts - start) * 0.001;
      material.uniforms.uMouse.value.set(pointer.x, 1 - pointer.y);
      var elapsed = Math.min(ts - cycleStart, CYCLE_MS);
      var envelope = Math.sin(Math.PI * (elapsed / CYCLE_MS));
      material.uniforms.uEnergy.value = BASE_ENERGY + (PEAK_ENERGY - BASE_ENERGY) * envelope;
      renderer.render(scene, camera);
      rafId = requestAnimationFrame(frame);
    }
    function loopStart() { if (!running) { running = true; start = null; rafId = requestAnimationFrame(frame); } }
    function loopStop() { running = false; if (rafId) cancelAnimationFrame(rafId); rafId = null; }

    // A fire-and-forget animation: once triggered, it plays out in full
    // regardless of whether the pointer stays over the diamond - hovering
    // off partway through no longer cuts the build short, it just keeps
    // going and fades itself out at the end of its own cycle.
    var cycleRunning = false;
    function runCycle() {
      if (cycleRunning) return;
      cycleRunning = true;
      cycleStart = null;
      loopStart();
      heroGlowCanvas.classList.add('is-active');
    }
    heroGlowCanvas.addEventListener('animationend', function () {
      heroGlowCanvas.classList.remove('is-active');
      loopStop();
      cycleRunning = false;
    });

    diamondTrigger.addEventListener('pointerenter', runCycle);

    document.addEventListener('visibilitychange', function () { if (document.hidden) loopStop(); });
  }

  /* ======================================================================
     3. SUBMIT - a plain, honest submit: disable the button and swap its
        label so there's feedback while the real POST/redirect happens, no
        artificial delay.
     ====================================================================== */
  function initSubmit() {
    if (!form || !submitBtn) return;
    form.addEventListener('submit', function () {
      if (submitBtn.disabled) return;
      submitBtn.disabled = true;
      var label = submitBtn.querySelector('[data-submit-label]');
      if (label) label.textContent = 'Sending...';
    });
  }

  function boot() {
    initAmbient();
    initHeroGlow();
    initSubmit();
  }

  if (document.readyState === 'complete') { boot(); }
  else { window.addEventListener('load', boot); }
})();
